import React, { useState, useRef, useEffect, useMemo } from 'react';
import ExcelTable from './ExcelTable';
import { tableAPI } from '../services/api';
import './Table.css';

const Table2 = () => {
  // Load saved name from localStorage or use default
  const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
  const [tableName, setTableName] = useState(savedNames[2] || 'Expense List');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectionStats, setSelectionStats] = useState(null);

  // Sync table name from localStorage when it changes
  useEffect(() => {
    const handleNameUpdate = () => {
      const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
      const newName = savedNames[2] || 'Expense List';
      setTableName(prevName => {
        if (prevName !== newName) {
          return newName;
        }
        return prevName;
      });
    };
    
    window.addEventListener('defaultTableNameUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('defaultTableNameUpdated', handleNameUpdate);
    };
  }, []);

  // Get initial data - ONLY headers, don't load from localStorage
  // Data should come from MySQL via ExcelTable component
  const getInitialData = () => {
    // Return empty table with headers only
    // ExcelTable will load actual data from MySQL
    return [['Sno', 'Date', 'Purpose', 'Currency', 'Amount', 'Notes']];
  };

  const initialData = getInitialData();

  // Currency column is at index 3 (0-indexed)
  const currencyColumns = [3];
  // Total column: amount (4) - removed, shown as stats above title
  const totalColumns = [];
  
  // Calculate totals from table data, grouped by currency
  // Fetch from MySQL first, fallback to localStorage
  const [tableDataForTotals, setTableDataForTotals] = useState(null);
  
  useEffect(() => {
    const fetchDataForTotals = async () => {
      try {
        // Try MySQL first
        const mysqlData = await tableAPI.getById('table2');
        if (mysqlData && mysqlData.length > 1) {
          setTableDataForTotals(mysqlData);
          // Sync to localStorage
          localStorage.setItem('table_table2', JSON.stringify(mysqlData));
        } else {
          // MySQL empty - clear localStorage and set empty
          localStorage.removeItem('table_table2');
          setTableDataForTotals(null);
        }
      } catch (error) {
        // MySQL failed - try localStorage but validate
        const localData = localStorage.getItem('table_table2');
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            if (parsed && parsed.length > 1) {
              setTableDataForTotals(parsed);
            } else {
              localStorage.removeItem('table_table2');
              setTableDataForTotals(null);
            }
          } catch (e) {
            localStorage.removeItem('table_table2');
            setTableDataForTotals(null);
          }
        } else {
          setTableDataForTotals(null);
        }
      }
    };
    
    fetchDataForTotals();
    // Refresh every 2 seconds to sync with MySQL
    const interval = setInterval(fetchDataForTotals, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const calculateTotals = () => {
    try {
      // Use fetched data
      const parsed = tableDataForTotals;
      if (!parsed || parsed.length < 2) return { totalAmount: 0, currencyBreakdown: {} };
      
      const headers = parsed[0];
      const currencyIndex = headers.indexOf('Currency');
      const amountIndex = headers.indexOf('Amount');
      
      let totalAmount = 0;
      let currencyBreakdown = {};
      
      parsed.slice(1).forEach(row => {
        if (row && row.length > 0) {
          // Get currency from the currency column - must be explicitly set (not empty)
          const rowCurrency = currencyIndex !== -1 && row[currencyIndex] 
            ? String(row[currencyIndex]).toUpperCase().trim() 
            : '';
          
          // Always add to total amount
          if (amountIndex !== -1 && row[amountIndex]) {
            const value = parseFloat(row[amountIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
            totalAmount += value;
          }
          
          // Only track non-INR currencies that are explicitly set (not empty, not INR)
          if (rowCurrency && rowCurrency !== 'INR' && rowCurrency !== '' && currencyIndex !== -1) {
            if (!currencyBreakdown[rowCurrency]) {
              currencyBreakdown[rowCurrency] = { totalAmount: 0 };
            }
            
            // Add to this currency's breakdown
            if (amountIndex !== -1 && row[amountIndex]) {
              const value = parseFloat(row[amountIndex].toString().replace(/[^0-9.-]/g, '') || '0') || 0;
              currencyBreakdown[rowCurrency].totalAmount += value;
            }
          }
        }
      });
      
      // Clean up currency breakdown - remove currencies with zero values
      Object.keys(currencyBreakdown).forEach(currency => {
        if (currencyBreakdown[currency].totalAmount === 0) {
          delete currencyBreakdown[currency];
        }
      });
      
      return { totalAmount, currencyBreakdown };
    } catch (e) {
      console.error('Error calculating totals:', e);
      return { totalAmount: 0, currencyBreakdown: {} };
    }
  };

  const totals = useMemo(() => calculateTotals(), [tableDataForTotals]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Save updated name to localStorage for sidebar sync
    const savedNames = JSON.parse(localStorage.getItem('defaultTableNames') || '{}');
    savedNames[2] = tableName; // Table2 has id 2
    localStorage.setItem('defaultTableNames', JSON.stringify(savedNames));
    // Dispatch custom event to notify sidebar
    window.dispatchEvent(new Event('defaultTableNameUpdated'));
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {isEditingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                className="table-name-input"
                maxLength={50}
              />
            ) : (
              <h1 onDoubleClick={handleNameDoubleClick} className="table-name-editable" title="Double-click to edit" style={{ margin: 0 }}>
                {tableName}
                <span className="edit-icon">✎</span>
              </h1>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap', marginLeft: 'auto' }}>
              {/* Calculate INR total (all amounts minus non-INR currencies) */}
              {(() => {
                const inrTotal = totals.totalAmount - (totals.currencyBreakdown && Object.keys(totals.currencyBreakdown).length > 0 
                  ? Object.values(totals.currencyBreakdown).reduce((sum, val) => sum + (val.totalAmount || 0), 0)
                  : 0);
                const aedTotal = totals.currencyBreakdown?.AED?.totalAmount || 0;
                
                return (
                  <>
                    {inrTotal !== 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>INR:</span>
                <span style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '700' }}>
                          ₹{inrTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                        {selectionStats && selectionStats[4] && selectionStats[4].hasNumeric && selectionStats[4].byCurrency && (
                          <div style={{ fontSize: '0.7rem', color: '#0284c7', fontWeight: '500', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e0f2fe', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {selectionStats[4].byCurrency.INR && (
                              <>
                              <span>Selected INR: ₹{selectionStats[4].byCurrency.INR.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: ₹{selectionStats[4].byCurrency.INR.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                            {selectionStats[4].byCurrency.AED && (
                              <>
                              <span>Selected AED: AED {selectionStats[4].byCurrency.AED.sum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Avg: AED {selectionStats[4].byCurrency.AED.average.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {aedTotal !== 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>AED:</span>
                        <span style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '700' }}>
                          AED {aedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
              </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      <ExcelTable 
        initialData={initialData} 
        currencyColumns={currencyColumns}
        fixedHeaders={true}
        totalColumns={totalColumns}
        tableId="table2"
        tableName={tableName}
        onSelectionStatsChange={setSelectionStats}
      />
    </div>
  );
};

export default Table2;

